import { FC, MouseEvent, useEffect, useState } from 'react';
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { TUserDocumentSummaryMap } from '../../../../../apis/documents/documents.interfaces';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { values, isEmpty } from 'lodash-es';
import {
  TDocumentStatus,
  TDocumentType,
} from '../../../../../apis/documents/documents.interfaces';
import {
  useStaffDocumentsActions,
  useStaffDocumentsState,
} from '../../contexts/staffDocuments.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';
import DocumentDetailsComponent from '../../../../components/documentDetails/documentDetails.component';
interface StaffDocumentsGridProps {
  isLoading?: boolean;
}

const StaffDocumentsGrid: FC<StaffDocumentsGridProps> = () => {
  const {
    staffDocumentRecords,
    simplifiedStaffObject,
    staffDocumentsAppState,
  } = useStaffDocumentsState();
  const {
    setIsManageDocumentOpen,
    setSelectedDocumentId,
    setIsCreateMode,
    setIsEditMode,
    setSelectedDocument,
  } = useStaffDocumentsActions();
  const { translateText } = useTranslation();

  const [documentsViewType, setDocumentsViewType] = useState<
    TDocumentStatus | 'all'
  >('all');
  const [documentTypeFilter, setDocumentTypeFilter] = useState<
    TDocumentType | 'payslips' | 'hr' | 'all'
  >('all');
  const [filteredDocuments, setFilteredDocuments] = useState<
    TUserDocumentSummaryMap[]
  >([]);

  const onAddDocumentClickHandler = () => {
    setIsManageDocumentOpen(true);
    setIsCreateMode(true);
    setIsEditMode(false);
  };

  const onEditDocumentClickHandler = () => {
    setIsManageDocumentOpen(true);
    setIsCreateMode(false);
    setIsEditMode(true);
  };

  const handleDocumentsViewChange = (
    _: MouseEvent<HTMLElement>,
    newViewType: TDocumentStatus | 'all'
  ) => {
    setDocumentsViewType(newViewType);
  };

  const handleRowClick = (params: any) => {
    const document = filteredDocuments.find((doc) => doc.id === params.row.id);
    if (document && document.documents[0]) {
      setSelectedDocumentId(document.documents[0].documentId);
      setSelectedDocument(document.documents[0]);
    }
  };

  const handleBackToDocuments = () => {
    setSelectedDocumentId(null);
    setSelectedDocument(null);
    setIsCreateMode(false);
    setIsEditMode(false);
    setIsManageDocumentOpen(false);
  };

  const getDocumentsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'basicDetails.name',
        headerName: translateText('staff.documents.columns.staffName'),
        width: 200,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return `${data.basicDetails.firstName} ${data.basicDetails.middleName || ''} ${data.basicDetails.lastName}`;
        },
      },
      {
        field: 'documentType',
        headerName: translateText('staff.documents.columns.documentType'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.documentType;
        },
      },
      {
        field: 'submissionDate',
        headerName: translateText('staff.documents.columns.submissionDate'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.submissionDate;
        },
      },
      {
        field: 'status',
        headerName: translateText('staff.documents.columns.status'),
        width: 120,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.status;
        },
      },
      {
        field: 'verificationDate',
        headerName: translateText('staff.documents.columns.verificationDate'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TUserDocumentSummaryMap;
          return data.documents[0]?.verificationDate || '-';
        },
      },
    ];
  };

  useEffect(() => {
    const getDocumentsGridRowsToRender = () => {
      if (isEmpty(staffDocumentRecords)) return [];

      const documentMap: Record<string, TUserDocumentSummaryMap> = {};

      staffDocumentRecords.forEach((eachStaffDoc) => {
        const basicDetails =
          simplifiedStaffObject[eachStaffDoc.userId]?.basicDetails;
        if (!basicDetails) return;
        documentMap[eachStaffDoc.userId] = {
          id: eachStaffDoc.userId,
          basicDetails: {
            firstName: basicDetails.firstName,
            middleName: basicDetails.middleName ?? undefined,
            lastName: basicDetails.lastName,
          },
          documents: [eachStaffDoc],
        };
      });

      setFilteredDocuments(values(documentMap));
    };

    getDocumentsGridRowsToRender();
  }, [staffDocumentRecords, simplifiedStaffObject]);

  const selectedDocumentId = staffDocumentsAppState.selectedDocumentId;
  const selectedDocument = selectedDocumentId
    ? filteredDocuments.find(
      (doc) => doc.documents[0]?.documentId === selectedDocumentId
    )
    : null;

  return (
    <Grid container spacing={{ xs: 1, sm: 2 }}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'stretch', sm: 'space-between' },
          gap: { xs: 1, sm: 2 },
          mb: { xs: 1, sm: 2 },
        }}
        data-testid="staff-documents-header-container"
      >
        {selectedDocument ? (
          <>
            <Box
              display="flex"
              alignItems="center"
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToDocuments}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  minHeight: { xs: '44px', sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                }}
                data-testid="back-to-documents-button"
              >
                {translateText('staff.documents.backToDocuments')}
              </Button>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                }}
                data-testid="document-details-title"
              >
                {translateText('staff.documents.documentDetails')}
              </Typography>
            </Box>
            <EditButton
              label={translateText('staff.documents.editDocument')}
              onClickHandler={onEditDocumentClickHandler}
              sx={{
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="edit-document-button"
            />
          </>
        ) : (
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            data-testid="staff-documents-filters-container"
          >
            <ToggleButtonGroup
              size="medium"
              color="primary"
              exclusive
              value={documentsViewType}
              onChange={handleDocumentsViewChange}
              aria-label="Documents View"
              sx={{
                height: 'var(--tab-group-height)',
                '& .MuiToggleButton-root': {
                  minHeight: { xs: '44px', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                },
              }}
              data-testid="staff-documents-view-toggle-button-group"
            >
              <ToggleButton
                value="all"
                data-testid="staff-documents-view-toggle-button-all"
              >
                {translateText('staff.documents.allDocuments')}
              </ToggleButton>
              <ToggleButton
                value={TDocumentStatus.PENDING}
                data-testid="staff-documents-view-toggle-button-pending"
              >
                {translateText('staff.documents.pending')}
              </ToggleButton>
              <ToggleButton
                value={TDocumentStatus.VERIFIED}
                data-testid="staff-documents-view-toggle-button-verified"
              >
                {translateText('staff.documents.verified')}
              </ToggleButton>
              <ToggleButton
                value={TDocumentStatus.REJECTED}
                data-testid="staff-documents-view-toggle-button-rejected"
              >
                {translateText('staff.documents.rejected')}
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>
                {translateText('staff.filters.documentType')}
              </InputLabel>
              <Select
                value={documentTypeFilter}
                label={translateText('staff.filters.documentType')}
                onChange={(e) =>
                  setDocumentTypeFilter(
                    e.target.value as
                    | TDocumentType
                    | 'payslips'
                    | 'hr'
                    | 'all'
                  )
                }
                data-testid="staff-documents-type-filter"
              >
                <MenuItem value="all">
                  {translateText('common.all')}
                </MenuItem>
                <MenuItem value="payslips">
                  {translateText('staff.filters.payslips')}
                </MenuItem>
                <MenuItem value={TDocumentType.HR_DOCUMENT}>
                  {translateText('staff.filters.hr')}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        {!selectedDocument && (
          <AddButton
            label={translateText('staff.documents.addDocument')}
            onClickHandler={onAddDocumentClickHandler}
            sx={{
              width: { xs: '100%', sm: 'auto' },
            }}
            data-testid="staff-documents-add-button"
          />
        )}
      </Grid>
      {selectedDocumentId ? (
        <Grid item xs={12}>
          <DocumentDetailsComponent documentId={selectedDocumentId} />
        </Grid>
      ) : (
        <Grid
          item
          xs={12}
          sx={{
            height: `calc(100% - var(--tab-group-height))`,
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            data-testid="staff-documents-grid"
            rows={filteredDocuments.filter((doc) => {
              const docType = doc.documents[0]?.documentType;
              const statusMatch =
                documentsViewType === 'all' ||
                doc.documents[0]?.status === documentsViewType;
              const typeMatch =
                documentTypeFilter === 'all' ||
                (documentTypeFilter === 'payslips' &&
                  docType === TDocumentType.SALARY_SLIP) ||
                (documentTypeFilter === TDocumentType.HR_DOCUMENT &&
                  docType === TDocumentType.HR_DOCUMENT);
              return statusMatch && typeMatch;
            })}
            columns={getDocumentsGridColumns()}
            onRowClick={handleRowClick}
            hideColumnsOnMobile={['verificationDate', 'submissionDate']}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default StaffDocumentsGrid;
